import { ActivityLog, Applicant } from 'types/graphql'

import ActivityTimestamp from 'src/components/ActivityLogs/shared/ActivityTimestamp'
import ActorBadge from 'src/components/ActivityLogs/shared/ActorBadge'
import { useCallDrawerContext } from 'src/context/CallDrawerContext'
import {
  ActivityLogActorType,
  CallDirection,
  CallStatus,
  CallType,
} from 'src/graphql/types/activityLogs'
import { CallConnectedStatus } from 'src/graphql/types/callActivity'
import {
  formatCallDuration,
  getCallDurationSeconds,
} from 'src/lib/activityLogs/callDuration'
import { buildFullName } from 'src/lib/formatters'
import { getNegativeVoEOutcomeLabel } from 'src/lib/voe/callOutcomeTag'

interface CallLogCardProps {
  activityLog: ActivityLog
  applicant?: Applicant
}

type PillStyle = { label: string; bg: string; color: string; border: string }

// Soft outcome pills — same palette family as the SMS "Pending Review" tag and
// the actor badges, so a call's result reads as a finished UI element rather
// than a colored word. Direction is conveyed by the bubble side, not the pill.
const SUCCESS: PillStyle = {
  label: 'Connected',
  bg: '#ECFDF3',
  color: '#067647',
  border: '#ABEFC6',
}
const DANGER = (label: string): PillStyle => ({
  label,
  bg: '#FEF3F2',
  color: '#B42318',
  border: '#FECDCA',
})
const VOICEMAIL: PillStyle = {
  label: 'Voicemail',
  bg: '#FFFAEB',
  color: '#B54708',
  border: '#FEDF89',
}
const PROGRESS: PillStyle = {
  label: 'In progress',
  bg: '#F2F4F7',
  color: '#475467',
  border: '#E4E7EC',
}
const INFO = (label: string): PillStyle => ({
  label,
  bg: '#EFF8FF',
  color: '#175CD3',
  border: '#B2DDFF',
})
const NEUTRAL = (label: string): PillStyle => ({
  label,
  bg: '#F2F4F7',
  color: '#475467',
  border: '#E4E7EC',
})

const STATUS_PILL: Partial<Record<CallStatus, PillStyle>> = {
  [CallStatus.COMPLETED]: SUCCESS,
  [CallStatus.NO_ANSWER]: DANGER('No answer'),
  [CallStatus.BUSY]: DANGER('Busy'),
  [CallStatus.FAILED]: DANGER('Failed'),
  [CallStatus.LEFT_VOICEMAIL]: VOICEMAIL,
  [CallStatus.LEAVING_VOICEMAIL]: VOICEMAIL,
  [CallStatus.INITIATED]: PROGRESS,
  [CallStatus.RINGING]: PROGRESS,
  [CallStatus.IN_PROGRESS]: PROGRESS,
}

// The outcome of a connected conversation (mostly set by AI voice-agent calls):
// did it book, need a callback, get transferred, etc. Shown alongside the
// "Connected" pill so a connected call reveals what actually happened.
const CONNECTED_RESULT_PILL: Partial<Record<CallConnectedStatus, PillStyle>> = {
  [CallConnectedStatus.BOOKED]: { ...SUCCESS, label: 'Booked' },
  [CallConnectedStatus.CALLBACK]: INFO('Callback'),
  [CallConnectedStatus.TRANSFERRED]: INFO('Transferred'),
  [CallConnectedStatus.NOT_INTERESTED]: DANGER('Not interested'),
  [CallConnectedStatus.DISQUALIFIED]: DANGER('Disqualified'),
  [CallConnectedStatus.NOT_AVAILABLE]: NEUTRAL('Not available'),
  [CallConnectedStatus.OTHER]: NEUTRAL('Other'),
  [CallConnectedStatus.VERIFIED]: { ...SUCCESS, label: 'Verified' },
  [CallConnectedStatus.INFO_COLLECTED]: INFO('Info collected'),
  [CallConnectedStatus.VOICEMAIL]: VOICEMAIL,
}

const Pill = ({ pill }: { pill: PillStyle }) => (
  <span
    className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium leading-tight"
    style={{
      backgroundColor: pill.bg,
      color: pill.color,
      borderColor: pill.border,
    }}
  >
    {pill.label}
  </span>
)

// Sidebar call bubble — the call-thread counterpart to SMSLog. Inbound calls
// sit left, outbound right; the actor (recruiter, AI voice agent, or applicant)
// reads like a teammate with an "AI"/"Manual"/"Bulk" badge, and the bubble
// shows the outcome as a soft pill plus the call length for connected calls.
const MiniCallLog = ({ activityLog, applicant }: CallLogCardProps) => {
  const { handleOpen } = useCallDrawerContext()

  const isOutbound = activityLog.callDirection === CallDirection.OUTBOUND
  const isVoECall = !!activityLog.voeId
  const isAICall = activityLog.callType === CallType.BLANDAI
  const isBulk = activityLog.actorType === ActivityLogActorType.BULK_ACTION

  const status = activityLog.callStatus
  // "Manual" means hand-registered via the Register Call form — those never
  // carry a callStatus. Real dialer calls (in- and outbound) also write
  // actorType MANUAL (twilio-inbound-call.ts / twilio-outbound-call.ts), so
  // actorType alone would badge genuine phone calls as Manual.
  const isManual =
    activityLog.actorType === ActivityLogActorType.MANUAL && !status
  const statusPill = status ? STATUS_PILL[status] : undefined
  const negativeVoEOutcome = getNegativeVoEOutcomeLabel(
    activityLog.connectedStatus,
    activityLog.voeFindings
  )
  const resultPill = negativeVoEOutcome
    ? INFO(negativeVoEOutcome)
    : activityLog.connectedStatus
    ? CONNECTED_RESULT_PILL[activityLog.connectedStatus]
    : undefined
  // A mailbox is not a connected conversation. Show the Voicemail chip on
  // its own — "Connected · Voicemail" would read as a live contact.
  const isVoicemailDisposition =
    activityLog.connectedStatus === CallConnectedStatus.VOICEMAIL
  // One pill, not two: a connected call with a recorded conversation result
  // reads "Connected · Booked", tinted by the result (mirrors the Calls
  // table's combined "status: result" label). A statusless call with a result
  // (e.g. a manually registered outcome) still surfaces the result pill.
  let pill = statusPill ?? resultPill
  if (isVoicemailDisposition) {
    pill = VOICEMAIL
  } else if (statusPill && resultPill && status === CallStatus.COMPLETED) {
    pill = { ...resultPill, label: `Connected · ${resultPill.label}` }
  }
  const isVoicemail =
    status === CallStatus.LEFT_VOICEMAIL ||
    status === CallStatus.LEAVING_VOICEMAIL

  // AI only labels our side — an AI agent places outbound calls; an inbound
  // call is the applicant calling in, so its left bubble always reads as the
  // applicant regardless of how we handled it.
  const isAIOutbound = isOutbound && isAICall

  // Who's on this bubble's side: outbound → the recruiter (or AI agent that
  // placed it); inbound → the applicant who called. Mirrors the SMS thread.
  const displayName = isOutbound
    ? isAICall
      ? activityLog.voiceAgent?.name || 'AI agent'
      : buildFullName(activityLog.employee)
    : applicant
    ? buildFullName(applicant)
    : ''

  // Length shows whenever the dialer / AI recorded one — connected talk time,
  // the voicemail being left, even ring time on busy/no-answer. Hand-logged
  // calls never capture a duration, so they simply omit it.
  const durationInSeconds = getCallDurationSeconds(activityLog)
  const showLength = !isNaN(durationInSeconds) && durationInSeconds > 0

  const voiceMailTitle = activityLog.voiceMailTemplate?.title

  return (
    <div
      className={`flex w-64 max-w-[75%] flex-col gap-1 ${
        isOutbound ? 'ml-auto' : 'mr-auto'
      }`}
    >
      {/* Name pinned left, timestamp pinned right — uniform across SMS/Call/
          Email threads regardless of which side the bubble sits on. */}
      <div className="flex w-full flex-row items-center justify-between gap-2 text-xs text-doubleNickel-gray-600">
        <div className="flex items-center gap-1.5">
          {displayName && <span className="font-medium">{displayName}</span>}
          {isAIOutbound && (
            <ActorBadge variant="ai" label="Placed by an AI voice agent" />
          )}
          {!isAIOutbound && isManual && (
            <ActorBadge
              variant="manual"
              label="Logged manually by a recruiter"
            />
          )}
          {!isAIOutbound && isBulk && (
            <ActorBadge
              variant="bulk"
              label="Logged as part of a bulk action"
            />
          )}
        </div>
        <ActivityTimestamp
          date={activityLog.activityDate ?? activityLog.createdAt}
        />
      </div>

      <div
        role="button"
        tabIndex={0}
        aria-label={`Open call details${
          displayName ? ` — ${displayName}` : ''
        }`}
        onClick={() => handleOpen(activityLog.activityLogId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleOpen(activityLog.activityLogId)
          }
        }}
        className={`flex cursor-pointer flex-col gap-1 rounded-t-lg p-3 transition-colors ${
          isOutbound
            ? 'rounded-bl-xl bg-doubleNickel-brand-25 hover:bg-doubleNickel-brand-50'
            : 'rounded-br-xl bg-doubleNickel-gray-50 hover:bg-doubleNickel-gray-100'
        }`}
      >
        {/* Fixed-width bubble: status pill pinned left, duration pinned right,
            so rows align across the thread no matter the label length. */}
        <div className="flex items-center justify-between gap-2">
          {/* Statusless calls (e.g. hand-logged before an outcome existed) still
              get a neutral pill so every bubble reads as a finished element. */}
          <Pill
            pill={
              pill ??
              NEUTRAL(
                isVoECall
                  ? 'VoE call'
                  : isOutbound
                  ? 'Outbound call'
                  : 'Inbound call'
              )
            }
          />
          {showLength && (
            <span className="whitespace-nowrap text-xs text-doubleNickel-gray-500">
              {formatCallDuration(durationInSeconds)}
            </span>
          )}
        </div>
        {isVoicemail && voiceMailTitle && (
          <div className="truncate text-xs text-doubleNickel-gray-400">
            “{voiceMailTitle}”
          </div>
        )}
        {isVoECall && activityLog.voe?.companyName && (
          <div className="truncate text-xs text-doubleNickel-gray-500">
            {activityLog.voe.companyName}
          </div>
        )}
      </div>
    </div>
  )
}

export default MiniCallLog
